import { apiClient } from '../apiClient';

export interface CreateOrgRequest {
  name: string;
  slug: string;
  logo_url?: string;
  admin: { email: string };
}

export interface OrgSummary {
  id: string;
  name: string;
  slug: string;
  logo_url?: string;
  created_at: string;
  org_admin_email?: string;
  batch_count: number;
  member_count: number;
  pending_count: number;
}

export interface OrgMemberRow {
  user_id: string;
  email: string;
  username: string;
  role: string;
  batch_id?: string;
  batch_name?: string;
}

export interface UserInvite {
  id: string;
  email: string;
  org_id?: string;
  role: string;
  expires_at: string;
}

export interface OrgDetail extends OrgSummary {
  members: OrgMemberRow[];
  pending_invites: UserInvite[];
}

export interface CreateBatchRequest {
  org_id: string;
  name: string;
  start_date?: string;
  end_date?: string;
}

export interface BatchTrainerListItem {
  user_id: string;
  username: string;
  email: string;
  status: 'active' | 'ended' | 'cancelled';
  start_date?: string;
  end_date?: string;
}

export interface BatchSummary {
  id: string;
  org_id: string;
  org_name: string;
  name: string;
  start_date?: string;
  end_date?: string;
  archived_at?: string;
  created_at: string;
  trainers: BatchTrainerListItem[];
}

export type BatchDetail = BatchSummary;

export interface AssignTrainerRequest {
  user_id: string;
  start_date?: string;
  end_date?: string;
}

export interface UpdateAssignmentRequest {
  status: 'active' | 'ended' | 'cancelled';
  end_date?: string;
}

export interface UpdateOrgRequest {
  name: string;
}

export interface UpdateBatchRequest {
  name?: string;
  start_date?: string;
  end_date?: string;
}

export interface CloneBatchRequest {
  org_id: string;
  name: string;
  start_date?: string;
  end_date?: string;
}

export interface BatchTrainerRow {
  id: string;
  batch_id: string;
  user_id: string;
  status: string;
  start_date?: string;
  end_date?: string;
  assigned_by: string;
  assigned_at: string;
}

export interface TrainerPoolEntry {
  user_id: string;
  username: string;
  email: string;
  active_assignments: number;
}

export interface AdminUserRow {
  user_id: string;
  email: string;
  username: string;
  role: string;
  org_id: string;
  org_name: string;
  status: string;
  batch_id: string;
  batch_name: string;
  batch_count: number;
  created_at: string;
  invite_id?: string;
  invite_expired?: boolean;
}

export interface UserMembership {
  org_id: string;
  org_name: string;
  batch_id: string;
  batch_name: string;
  role: string;
  status: string;
  joined_at: string;
}

export interface AdminUserDetail {
  user_id: string;
  email: string;
  username: string;
  role: string;
  created_at: string;
  memberships: UserMembership[];
}

export interface InviteUserRequest {
  email: string;
  username?: string;
  role: 'org_admin' | 'trainer' | 'student';
  org_id?: string;
  batch_id?: string;
}

export interface InviteUserResponse {
  invite_url: string;
  token: string;
  expires_at: string;
}

export interface SkippedInvite {
  email: string;
  reason: string;
}

export interface BulkInviteResponse {
  sent: string[];
  skipped: SkippedInvite[];
}

/** Everything a curriculum swap would permanently destroy. */
export interface CurriculumSwapPreview {
  has_curriculum: boolean;
  current_template_title?: string;
  modules: number;
  topics: number;
  sessions: number;
  resources: number;
  assessments: number;
  mcq_questions: number;
  visualizations: number;
  students_with_progress: number;
  progress_rows: number;
  submissions: number;
  messages: number;
  notes: number;
}

/** A batch whose curriculum can be copied onto another batch. */
export interface CurriculumSourceBatch {
  batch_id: string;
  name: string;
  org_name: string;
  template_title?: string;
  modules: number;
  topics: number;
  assessments: number;
  visualizations: number;
}

export interface SwapCurriculumRequest {
  source_kind: 'template' | 'batch';
  source_id: string;
}

export interface CurriculumSwapResult {
  batch_curriculum_id: string;
  copied: {
    modules: number;
    topics: number;
    sessions: number;
    resources: number;
    assessments: number;
    mcq_questions: number;
    visualizations: number;
  };
  deleted: { modules: number; topics: number };
}

export const adminApi = {
  createOrg: async (req: CreateOrgRequest) => {
    const { data } = await apiClient.post('/api/v1/admin/orgs', req);
    return data as { org_id: string; invite_url: string; expires_at: string };
  },
  listOrgs: async () => {
    const { data } = await apiClient.get('/api/v1/admin/orgs');
    return (data ?? []) as OrgSummary[];
  },
  getOrg: async (id: string) => {
    const { data } = await apiClient.get(`/api/v1/admin/orgs/${id}`);
    return data as OrgDetail;
  },
  deleteOrg: async (id: string) => {
    await apiClient.delete(`/api/v1/admin/orgs/${id}`);
  },
  updateOrg: async (id: string, req: UpdateOrgRequest) => {
    const { data } = await apiClient.patch(`/api/v1/admin/orgs/${id}`, req);
    return data as OrgSummary;
  },
  // --- Batches (Phase 2) ---
  createBatch: async (req: CreateBatchRequest) => {
    const { data } = await apiClient.post('/api/v1/admin/batches', req);
    return data as BatchSummary;
  },
  listBatches: async (orgId?: string, includeArchived?: boolean) => {
    const params = new URLSearchParams();
    if (orgId) params.set('org_id', orgId);
    if (includeArchived) params.set('include_archived', 'true');
    const url = `/api/v1/admin/batches${params.toString() ? `?${params}` : ''}`;
    const { data } = await apiClient.get(url);
    return (data ?? []) as BatchSummary[];
  },
  getBatch: async (id: string) => {
    const { data } = await apiClient.get(`/api/v1/admin/batches/${id}`);
    return data as BatchDetail;
  },
  deleteBatch: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/admin/batches/${id}`);
  },
  archiveBatch: async (id: string): Promise<void> => {
    await apiClient.patch(`/api/v1/admin/batches/${id}/archive`);
  },
  updateBatch: async (id: string, req: UpdateBatchRequest) => {
    const { data } = await apiClient.patch(`/api/v1/admin/batches/${id}`, req);
    return data as BatchSummary;
  },
  cloneBatch: async (sourceBatchId: string, req: CloneBatchRequest) => {
    const { data } = await apiClient.post(`/api/v1/admin/batches/${sourceBatchId}/clone`, req);
    return data as BatchSummary;
  },
  getBatchDeletionPreview: async (id: string): Promise<{ student_count: number; trainer_count: number }> => {
    const { data } = await apiClient.get(`/api/v1/admin/batches/${id}/deletion-preview`);
    return data as { student_count: number; trainer_count: number };
  },
  deleteBatchCurriculum: async (batchId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/admin/curriculum/batches/${batchId}/curriculum`);
  },
  getCurriculumSwapPreview: async (batchId: string) => {
    const { data } = await apiClient.get(`/api/v1/admin/batches/${batchId}/curriculum-swap-preview`);
    return data as CurriculumSwapPreview;
  },
  listCurriculumSourceBatches: async () => {
    const { data } = await apiClient.get('/api/v1/admin/batches/curriculum-sources');
    return (data ?? []) as CurriculumSourceBatch[];
  },
  swapBatchCurriculum: async (batchId: string, req: SwapCurriculumRequest) => {
    const { data } = await apiClient.post(`/api/v1/admin/batches/${batchId}/curriculum`, req);
    return data as CurriculumSwapResult;
  },
  assignTrainer: async (batchId: string, req: AssignTrainerRequest) => {
    const { data } = await apiClient.post(`/api/v1/admin/batches/${batchId}/trainers`, req);
    return data as BatchTrainerRow;
  },
  updateAssignment: async (batchId: string, userId: string, req: UpdateAssignmentRequest) => {
    const { data } = await apiClient.patch(`/api/v1/admin/batches/${batchId}/trainers/${userId}`, req);
    return data as BatchTrainerRow;
  },
  listTrainerPool: async () => {
    const { data } = await apiClient.get('/api/v1/admin/trainers');
    return (data ?? []) as TrainerPoolEntry[];
  },
  getUser: async (id: string) => {
    const { data } = await apiClient.get(`/api/v1/admin/users/${id}`);
    return data as AdminUserDetail;
  },
  listUsers: async (orgId?: string, batchId?: string) => {
    const params = new URLSearchParams();
    if (orgId) params.set('org_id', orgId);
    if (batchId) params.set('batch_id', batchId);
    const qs = params.size ? `?${params.toString()}` : '';
    const { data } = await apiClient.get(`/api/v1/admin/users${qs}`);
    return (data ?? []) as AdminUserRow[];
  },
  revokeEnrollment: async (userID: string, batchID: string): Promise<void> => {
    await apiClient.patch(`/api/v1/admin/users/${userID}/revoke-enrollment`, { batch_id: batchID });
  },
  revokeAssignment: async (userID: string, batchID: string): Promise<void> => {
    await apiClient.patch(`/api/v1/admin/users/${userID}/revoke-assignment`, { batch_id: batchID });
  },
  inviteUser: async (req: InviteUserRequest) => {
    const { data } = await apiClient.post('/api/v1/auth/invites', req);
    return data as InviteUserResponse;
  },
  bulkInvite: async (batchId: string, emails: string[]): Promise<BulkInviteResponse> => {
    const { data } = await apiClient.post('/api/v1/auth/invites/bulk', {
      batch_id: batchId,
      emails,
    });
    return data as BulkInviteResponse;
  },
  getBatchStudents: async (batchId: string): Promise<BatchEnrollmentStats> => {
    const { data } = await apiClient.get(`/api/v1/admin/batches/${batchId}/students`);
    return data as BatchEnrollmentStats;
  },
  resendInvite: async (inviteId: string): Promise<{ invite_url: string; expires_at: string }> => {
    const { data } = await apiClient.post(`/api/v1/admin/invites/${inviteId}/resend`);
    return data;
  },
  deleteInvite: async (inviteId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/auth/invites/${inviteId}`);
  },
  deleteUser: async (userId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/admin/users/${userId}`);
  },
  enrollStudent: async (batchId: string, req: { user_id: string }): Promise<EnrollStudentResponse> => {
    const { data } = await apiClient.post(`/api/v1/admin/batches/${batchId}/students`, req);
    return data as EnrollStudentResponse;
  },
};

export interface EnrollStudentResponse {
  batch_id: string;
  user_id: string;
  status: string;
  enrolled_at: string;
}

export interface StudentProgress {
  user_id: string;
  email: string;
  username: string;
  enrollment_status: string;
  enrolled_at: string;
  completed_topics: number;
  in_progress_topics: number;
  total_topics: number;
  completion_pct: number;
}

export interface CompletionSummary {
  not_started: number;
  just_started: number;
  in_progress: number;
  advanced: number;
  completed: number;
}

export interface BatchEnrollmentStats {
  batch_id: string;
  batch_name: string;
  total_topics: number;
  students: StudentProgress[];
  summary: CompletionSummary;
}
